import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { OddsService } from '../services/odds.service';
import { ActivatedRoute } from '@angular/router';
import { ParamsModel } from '../../match-details/models/params.model';
import { GroupedResult, OddsModel } from '../../../models/sports.model';
import { TranslationService } from '../../../services/translation.service';
import { CommonModule } from '@angular/common';
import { NzSkeletonComponent } from 'ng-zorro-antd/skeleton';
import { TranslateModule } from '@ngx-translate/core';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { ExpansionPanelComponent } from '../../../components/shared/expansion-panel/expansion-panel.component';
import { environment } from '../../../../environments/environment';
import { TabBarComponent } from '../../../components/shared/tab-bar/tab-bar.component';
import { TabsModel } from '../../../models/common.model';
import { NoResultComponent } from '../../../components/shared/no-result/no-result.component';

@Component({
  selector: 'app-match-odds',
  standalone: true,
  imports: [
    CommonModule,
    NzSkeletonComponent,
    TranslateModule,
    NzIconModule,
    ExpansionPanelComponent,
    TabBarComponent,
    NoResultComponent
  ],
  styleUrl: './match-odds.component.scss',
  templateUrl: './match-odds.component.html',
})
export class MatchOddsComponent implements OnInit, OnChanges {
  @Input() isMatch: boolean = false;

  oddsList: OddsModel[] = [];
  groupedOddsList: GroupedResult[] = [];
  isLoading: boolean = true;
  params: null | ParamsModel = null;

  urlIMG = environment.urlIMG;
  urlIcon = environment.urlIcon;

  isCollapsed: boolean = false;

  filters: TabsModel[] = [];
  singleOddDetail: any = null;
  selected: any = null;

  toggleCollapse(panel: OddsModel) {
    panel.active = !panel.active;
  }

  constructor(
    private oddsService: OddsService,
    private readonly translatorService: TranslationService,
    private readonly _activateRoute: ActivatedRoute
  ) {}

  ngOnInit(): void {
    if (this.isMatch)
      this._activateRoute.params.subscribe((params: any) => {
        this.params = params;
        this.getOddsData();
      });
    else this.getOddsData();
  }

  ngOnChanges(): void {}

  formatDate(dateString: any) {
    const date = new Date(dateString);

    const day = ('0' + date.getDate()).slice(-2);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);

    // const formattedDate = day + '/' + month + ' • ' + hours + ':' + minutes;
    const formattedDate = hours + ':' + minutes;

    return formattedDate;
  }

  extractWithinBraces(inputString: string): string {
    let match = inputString.match(/\(([^)]+)\)/);
    if (match) {
      return match[1];
    } else {
      return '';
    }
  }

  getOddsData() {
    if (this.isMatch && this.params) {
      this.oddsService
        .getMatchOdds(this.params?.idHome, this.params?.idAway)
        .subscribe(
          (response) => {
            if (response.status) {
              this.processOddsData(response.data);
            }
            this.isLoading = false;
          },
          (error) => {
            console.error('Error fetching match odds:', error);
            this.isLoading = false;
          }
        );
    } else {
      this.oddsService.getOdds().subscribe(
        (response) => {
          if (response.status) {
            this.processOddsData(response.data);
          }
          this.isLoading = false;
        },
        (error) => {
          console.error('Error fetching odds:', error);
          this.isLoading = false;
        }
      );
    }
  }

  groupData(data: any): GroupedResult[] {
    const groupedData: { [key: string]: GroupedResult } = {};

    data.forEach((item: any) => {
      const key = `${item.championshipsName}|${item.country}`;

      if (!groupedData[key]) {
        groupedData[key] = {
          championshipName: item.championshipsName,
          country: item.country,
          active: item.active,
          results: [],
        };
      }

      // Group details by type and typeId
      const groupedDetails: { [key: string]: any } = {};
      item.details.forEach((detail: any) => {
        const detailKey = `${detail.type}|${detail.typeId}`;
        if (!groupedDetails[detailKey]) {
          groupedDetails[detailKey] = {
            type: detail.type,
            typeId: detail.typeId,
            results: [],
          };
        }
        groupedDetails[detailKey].results.push({
          scores: detail.scores,
        });
      });

      groupedData[key].results.push({
        ...item,
        details: Object.values(groupedDetails),
      });
    });

    return Object.values(groupedData);
  }

  processOddsData(data: any[]) {
    this.oddsList = data.map((singleOddItem: any) => {
      const tempOdd: OddsModel = this.mapSingleOddItem(singleOddItem);
      return tempOdd;
    });

    this.groupedOddsList = this.groupData(this.oddsList);

    if (this.isMatch && this.groupedOddsList.length !== 0) {
      this.singleOddDetail = this.groupedOddsList[0]?.results[0]?.details;
      this.selected = this.singleOddDetail[0];
      this.filters = this.singleOddDetail?.map((singleItem: any) => ({
        title: singleItem.type,
      }));
    }
  }

  mapSingleOddItem(singleOddItem: any): OddsModel {
    const teams = singleOddItem.odd.eventName
      ? singleOddItem.odd.eventName.split(' vs. ')
      : singleOddItem.odd.eventName_es.split(' vs. ');

    const odds = singleOddItem.detail.find((x: any) => x.Name === '1x2');

    return {
      idOdds: singleOddItem.odd.id,
      active: this.isMatch,
      isLive: singleOddItem.odd.is_live,
      eventDate: this.formatDate(singleOddItem.odd.eventDate),
      championshipsName:
        singleOddItem.odd.championshipsName_es ||
        singleOddItem.odd.championshipsName,
      country: singleOddItem.odd.country_es || singleOddItem.odd.country,
      homeTeam: teams[0],
      awayTeam: teams[1],
      one: odds?.Selections.find((x: any) => x.SelectionTypeId === 1),
      x: odds?.Selections.find((x: any) => x.SelectionTypeId === 2),
      two: odds?.Selections.find((x: any) => x.SelectionTypeId === 3),

      details: singleOddItem.detail.map((x: any) => ({
        type: x.Name,
        typeId: x.MarketTypeid,
        scores: x.Selections.map((y: any) => ({
          text: y.Name,
          scores: y.Price,
        })),
      })),
    };
  }

  menuSelected(event: string) {
    this.selected = this.singleOddDetail.find((singleItem:any) => singleItem.type === event);
  }
}
